export interface EchoCheckResult {
  isEcho: boolean;
  confidence: number;
  reason: string;
}

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenSetRatio(a: string, b: string): number {
  const tokensA = new Set(normalize(a).split(" ").filter(Boolean));
  const tokensB = new Set(normalize(b).split(" ").filter(Boolean));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  const intersection = new Set([...tokensA].filter((x) => tokensB.has(x)));
  const union = new Set([...tokensA, ...tokensB]);
  return (intersection.size / union.size) * 100;
}

function partialRatio(a: string, b: string): number {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return 0;
  if (na.includes(nb) || nb.includes(na)) return 100;

  const wordsA = na.split(" ");
  const wordsB = nb.split(" ");
  let best = 0;
  for (let i = 0; i < wordsA.length; i++) {
    for (let j = 0; j < wordsB.length; j++) {
      let match = 0;
      while (
        i + match < wordsA.length &&
        j + match < wordsB.length &&
        wordsA[i + match] === wordsB[j + match]
      ) {
        match++;
      }
      best = Math.max(best, match);
    }
  }
  return (best / Math.max(wordsA.length, wordsB.length)) * 100;
}

export class VoiceEchoDetector {
  private lastTtsText = "";
  private ttsStartTime = 0;
  private ttsDurationSec = 0;
  private echoToleranceSec = 0.3;

  trackTtsStart(text: string, durationSec: number): void {
    this.lastTtsText = text;
    this.ttsStartTime = Date.now() / 1000;
    this.ttsDurationSec = durationSec;
  }

  trackTtsFinish(): void {
    this.ttsDurationSec = 0;
  }

  checkEcho(heardText: string, heardTime: number = Date.now() / 1000): EchoCheckResult {
    if (!this.lastTtsText) {
      return { isEcho: false, confidence: 0, reason: "No recent TTS" };
    }

    const timeSinceTtsStart = heardTime - this.ttsStartTime;
    const ttsEndTime = this.ttsStartTime + this.ttsDurationSec + this.echoToleranceSec;

    // If the transcript arrived well after TTS finished, it's not an echo
    if (heardTime > ttsEndTime + 1.0) {
      return { isEcho: false, confidence: 0, reason: "Transcript arrived after TTS window" };
    }

    // Fuzzy text comparison
    const partial = partialRatio(heardText, this.lastTtsText);
    const tokenSet = tokenSetRatio(heardText, this.lastTtsText);
    const score = Math.max(partial, tokenSet);

    // Time-based segment matching: estimate which part of the TTS was playing
    // when the transcript was captured and compare against that segment
    const ttsWords = normalize(this.lastTtsText).split(" ");
    const heardWords = normalize(heardText).split(" ");
    if (ttsWords.length > 0 && this.ttsDurationSec > 0 && timeSinceTtsStart > 0) {
      const wordsPerSec = ttsWords.length / this.ttsDurationSec;
      const estimatedWordIndex = Math.max(0, Math.floor((timeSinceTtsStart - this.echoToleranceSec) * wordsPerSec));
      const toleranceWords = Math.max(3, Math.round(this.echoToleranceSec * wordsPerSec)) + 3;
      const startIdx = Math.max(0, estimatedWordIndex - toleranceWords);
      const endIdx = Math.min(ttsWords.length, estimatedWordIndex + heardWords.length + toleranceWords);
      const segmentText = ttsWords.slice(startIdx, endIdx).join(" ");
      if (segmentText) {
        const segmentScore = Math.max(partialRatio(heardText, segmentText), tokenSetRatio(heardText, segmentText));
        if (segmentScore > score) {
          return { isEcho: segmentScore >= 75, confidence: segmentScore, reason: `Time-segment similarity ${segmentScore.toFixed(1)}%` };
        }
      }
    }

    return { isEcho: score >= 75, confidence: score, reason: `Overall similarity ${score.toFixed(1)}%` };
  }
}
